import { ChevronDownIcon, PlusIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { Ref, useCallback, useId, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import InvoiceLoader from '../../components/InvoiceLoader';
import { useExpenseActions } from '../../components/ExpenseActions';
import {
    AdvancedBillingToggle,
    CaseLabel,
    ITEM_FIGURE,
    ITEM_TOTALS_ROW,
    ItemBulkSelectButton,
    ItemCard,
    ItemHeadCells,
    ItemTable,
    TITLE_ACTION,
    useIsNarrowCard,
} from '../InvoiceItemTable';
import { InvoiceExpenseTableProps, useInvoiceExpenseTable } from './InvoiceExpenseTable';
import { InvoiceExpenseTableRow } from './InvoiceExpenseTableRow';
import { getExpenseTotals } from './InvoiceExpenseTableTotals';

/**
 * A case's expenses in the new design: a card titled by its case, its rows a windowed table, and the selection's
 * actions in place of the title row while rows are ticked. With no expenses it keeps its title and says so, with Add
 * expense where one can be added.
 */
export function InvoiceExpensesCard(props: InvoiceExpenseTableProps) {
    const {
        classifiers,
        addedInvoiceExpenseIds,
        isInViewMode,
        invoiceMatter,
        invoice,
        isLoading,
        validateForm,
        setValue,
        getValues,
        reset,
        control,
    } = props;
    const table = useInvoiceExpenseTable(props);
    const {
        buttonsDisabled,
        isExpenseEntryDisabled,
        canCreateExpenses,
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForExpenseId,
        clearModalRequest,
        isCorrectionInvoice,
        isPaymentCurrencyMode,
        displayCurrency,
        allowsNewExpenses,
        invoiceExpenseCount,
    } = table;
    const actions = useExpenseActions({
        ...table,
        handleMoveExpense: table.handleMove,
        control,
        getValues,
        invoiceAllowsNewExpenses: allowsNewExpenses,
        userCanCreateExpenses: canCreateExpenses,
    });
    const { cardRef, isNarrow } = useIsNarrowCard(advancedBillingEnabled);
    const titleId = useId();
    const intl = useIntl();
    const watchedExpenses = useWatch({ control, name: 'invoiceExpenses' });

    // A row waits until the form holds its expense, by the summary's order, which the form follows.
    const rowIndexes = useMemo(
        () => invoiceMatter.invoiceExpenses.flatMap((_, index) => (watchedExpenses?.[index] ? [index] : [])),
        [invoiceMatter.invoiceExpenses, watchedExpenses]
    );
    const getRowKey = useCallback(
        (position: number) => invoiceMatter.invoiceExpenses[rowIndexes[position]].id,
        [invoiceMatter.invoiceExpenses, rowIndexes]
    );
    const selectableIds = useMemo(
        () =>
            isInViewMode
                ? []
                : invoiceMatter.invoiceExpenses
                      .filter((item, index) => !item.isRemoved && watchedExpenses?.[index]?.editIntentTypeCode == null)
                      .map((item) => item.id),
        [isInViewMode, invoiceMatter.invoiceExpenses, watchedExpenses]
    );
    const selectedCount = (watchedExpenses ?? []).filter((item) => item.selected).length;
    const isAllSelected = selectedCount > 0 && selectedCount === selectableIds.length;

    if (isLoading) {
        return <InvoiceLoader />;
    }

    const showsActions = !isInViewMode || isCorrectionInvoice;
    const columnCount = isNarrow ? 3 : advancedBillingEnabled ? 6 : 5;
    const { totalAmount, totalWithVat, totalWithoutVat } = getExpenseTotals(
        invoiceMatter.invoiceExpenses,
        isPaymentCurrencyMode
    );
    const { areActionsDisabled } = actions;

    const addButton = allowsNewExpenses ? (
        <Tooltip
            content={
                isExpenseEntryDisabled
                    ? t('expenses.integration-entry-disabled')
                    : t('invoices.tooltips.add-new-expense')
            }
        >
            <Button
                variant="ghost"
                size="sm"
                disabled={isExpenseEntryDisabled || isInViewMode || !canCreateExpenses || buttonsDisabled}
                focusableWhenDisabled
                onClick={table.openExpenseModal}
                className={`${TITLE_ACTION} text-primary`}
            >
                <PlusIcon />
                <FormattedMessage id="expenses.add" />
            </Button>
        </Tooltip>
    ) : null;

    const titleActions =
        invoiceExpenseCount === 0 ? (
            addButton
        ) : (
            <>
                {addButton}
                {/* Only where a row can be selected: in view mode nothing can, and the selection could not act. */}
                {selectableIds.length ? (
                    <ItemBulkSelectButton disabled={buttonsDisabled} onClick={table.handleBulkSelect} />
                ) : null}
                <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_EXPENSE_EXTRA_OPTIONS}>
                    <AdvancedBillingToggle
                        checked={advancedBillingEnabled}
                        onCheckedChange={setAdvancedBillingEnabled}
                        disabled={isInViewMode}
                    />
                </FeatureFlagged>
            </>
        );

    const selectionActions = (
        <>
            <Button variant="outline" size="sm" disabled={areActionsDisabled} onClick={actions.openExpenseUpdateModal}>
                <FormattedMessage id="invoices.tooltips.move-to-another-case" />
            </Button>
            {isCorrectionInvoice ? (
                <Button variant="outline" size="sm" disabled={areActionsDisabled} onClick={table.handleRemove}>
                    <FormattedMessage id="invoices.tooltips.remove-via-correction" />
                </Button>
            ) : (
                <FeatureFlagged action="show" flag={FeatureFlag.DISCARD_TIMECARDS_EXPENSES_ON_INVOICE}>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={areActionsDisabled}
                        onClick={actions.openBulkDiscardConfirmationModal}
                    >
                        <FormattedMessage id="invoices.tooltips.discard-expenses" />
                    </Button>
                </FeatureFlagged>
            )}
            <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant="outline" size="sm" disabled={areActionsDisabled} />}>
                    <FormattedMessage id="tabs.more" />
                    <ChevronDownIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={table.handlePostpone}>
                        <FormattedMessage id="invoices.tooltips.postpone" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={table.handleNonBillable}>
                        <FormattedMessage id="invoices.tooltips.mark-as-non-billable" />
                    </DropdownMenuItem>
                    {allowsNewExpenses ? (
                        <DropdownMenuItem
                            disabled={isExpenseEntryDisabled || actions.isMultipleSelection || !canCreateExpenses}
                            onClick={table.handleDuplicate}
                        >
                            <FormattedMessage id="invoices.tooltips.duplicate" />
                        </DropdownMenuItem>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );

    // The total, and before it the VAT-applied and VAT-exempt parts it sums.
    const footRow = (labelId: string, amount: number, isTotal = false) => {
        const figure = <MoneyDisplay amount={amount} currency={displayCurrency} />;
        return (
            <tr className={ITEM_TOTALS_ROW}>
                <th
                    scope="row"
                    colSpan={2}
                    className={cn(
                        'text-meta ps-5 pe-2 text-start',
                        isTotal ? 'py-3 font-semibold' : 'py-2 font-normal'
                    )}
                >
                    <span className="flex items-baseline justify-between gap-3">
                        <FormattedMessage id={labelId} />
                        {isNarrow ? <span className="text-body1 tabular-nums">{figure}</span> : null}
                    </span>
                </th>
                {isNarrow ? null : (
                    <>
                        <td />
                        {advancedBillingEnabled ? <td /> : null}
                        <td
                            className={cn(
                                'px-2 text-end',
                                isTotal ? `${ITEM_FIGURE} py-3` : 'text-body1 py-2 tabular-nums'
                            )}
                        >
                            {figure}
                        </td>
                    </>
                )}
                <td />
            </tr>
        );
    };

    const renderRow = (position: number, measureRef: Ref<HTMLTableRowElement>) => {
        const index = rowIndexes[position];
        const field = invoiceMatter.invoiceExpenses[index];
        return (
            <InvoiceExpenseTableRow
                key={field.id}
                measureRef={measureRef}
                virtualIndex={position}
                isNarrow={isNarrow}
                readonly={isInViewMode}
                index={index}
                invoiceExpense={field}
                expenseForm={watchedExpenses[index]}
                invoice={invoice}
                classifiers={classifiers}
                isNew={field.expense !== null ? addedInvoiceExpenseIds.includes(field.expense.id ?? 0) : false}
                control={control}
                validateForm={validateForm}
                setValue={setValue}
                getValues={getValues}
                reset={reset}
                handleUpdateInvoiceExpenses={(idList) => {
                    void table.handleUpdateInvoiceExpenses(idList);
                }}
                advancedBillingEnabled={advancedBillingEnabled}
                shouldOpenModal={field.expense?.id === openModalForExpenseId}
                clearModalRequest={clearModalRequest}
            />
        );
    };

    return (
        <ItemCard
            cardRef={cardRef}
            titleId={titleId}
            title={t('expenses.expenses')}
            count={invoiceExpenseCount}
            caseLabel={<CaseLabel number={invoiceMatter.matterNumber} name={invoiceMatter.matterName} />}
            actions={titleActions}
            selectedCount={showsActions ? selectedCount : 0}
            selectedMessage={intl.formatMessage({ id: 'invoices.details.expenses-selected' }, { count: selectedCount })}
            selectionLabel={t('invoices.details.expenses-selection-actions')}
            selectionActions={selectionActions}
            emptyMessage={<FormattedMessage id="invoices.details.no-expenses" />}
        >
            <ItemTable
                caption={t('invoices.details.expenses-caption', {
                    case: `${invoiceMatter.matterNumber} ${invoiceMatter.matterName}`,
                })}
                columnCount={columnCount}
                rowCount={rowIndexes.length}
                getRowKey={getRowKey}
                renderRow={renderRow}
                head={
                    <ItemHeadCells
                        selectAll={
                            selectableIds.length ? (
                                <Checkbox
                                    aria-label={t('invoices.details.table.select-all-expenses')}
                                    checked={isAllSelected}
                                    indeterminate={selectedCount > 0 && !isAllSelected}
                                    disabled={buttonsDisabled}
                                    onCheckedChange={() => table.onBulkSelect(!isAllSelected, selectableIds)}
                                />
                            ) : null
                        }
                        entryLabelId="expenses.expense"
                        figureLabelId="invoices.original-amount"
                        hasAdvancedColumn={advancedBillingEnabled}
                        currency={displayCurrency}
                        isNarrow={isNarrow}
                    />
                }
                foot={
                    <>
                        {footRow('invoices.details.exp-total-with-vat', totalWithVat)}
                        {footRow('invoices.details.exp-total-wo-vat', totalWithoutVat)}
                        {footRow('invoices.totals-tab.table-headings.total', totalAmount, true)}
                    </>
                }
            />
        </ItemCard>
    );
}
